import dynamic from 'next/dynamic'
import {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'

import { NeedSettingPersonalUserSnackbar } from '@/components/organisms/membership/NeedSettingPersonalUserSnackbar'
import { needSettingPersonalUserSnackbarRef } from '@/components/organisms/membership/NeedSettingPersonalUserSnackbar/useNeedSettingPersonalUserSnackbar'
import { config } from '@/config'
import { handleChunkLoadError } from '@/lib/errorHandler'

import { useMembershipViewModel } from './viewModel'

type Options = {
  showSnackbarWhenClose?: boolean
  isRegisterNewMembership?: boolean
  onClose?: () => void
}

type SettingUserInfoModalContext = {
  isOpen: boolean
  showModal: (options?: Options) => void
  isSuccessModalOpen: boolean
}

const Context = createContext({} as SettingUserInfoModalContext)

export function useSettingUserInfoModal() {
  return useContext(Context)
}

type SettingUserInfoModalProviderProps = PropsWithChildren

export const SettingUserInfoModalProvider: FC<SettingUserInfoModalProviderProps> =
  ({ children }) => {
    const [open, setOpen] = useState<boolean>(false)
    const [isSuccessModalOpen, setSuccessModalOpen] = useState<boolean>(false)
    const [showOptions, setShowOptions] = useState<Options | undefined>()
    const { state } = useMembershipViewModel()

    // 個人情報の状態に基づいてモーダルの初期表示状態を設定
    useEffect(() => {
      if (
        state.user?.isMembershipPersonalInformationRequired &&
        !state.isPersonalInfoComplete
      ) {
        setOpen(true)
      }
    }, [state.user, state.isPersonalInfoComplete])

    const showModal = useCallback((options?: Options) => {
      setOpen(true)
      setShowOptions(options)
    }, [])

    return (
      <Context.Provider
        value={{
          showModal,
          isOpen: open,
          isSuccessModalOpen,
        }}
      >
        {children}
        <MembershipUserInfoModal
          open={open}
          onClose={() => {
            setOpen(false)
            showOptions?.onClose?.()
            if (showOptions?.showSnackbarWhenClose) {
              needSettingPersonalUserSnackbarRef.current?.triggerShowMessage(
                showOptions?.isRegisterNewMembership
              )
            }
          }}
          onCompleted={() => setOpen(false)}
          registerUserInfoSuccessModal={isSuccessModalOpen}
          setRegisterUserInfoSuccessModal={setSuccessModalOpen}
        />
        {state.user && <NeedSettingPersonalUserSnackbar user={state.user} />}
      </Context.Provider>
    )
  }

const MembershipUserInfoModal = dynamic<{
  open: boolean
  onClose: () => void
  onCompleted: () => void
  registerUserInfoSuccessModal: boolean
  setRegisterUserInfoSuccessModal: (open: boolean) => void
}>(
  config.modules.membership
    ? import('@/components/organisms/membership/MembershipUserInfo')
        .then(m => m.MembershipUserInfoModal)
        .catch(async error => {
          await handleChunkLoadError(error)
          return () => null
        })
    : Promise.resolve(() => null),
  {
    ssr: false,
  }
)
